import { useMutation, useQuery } from '@tanstack/react-query';
import { List, ListItem, Toggle } from 'framework7-react';
import {
  Component,
  ComponentProps,
  FC,
  Fragment,
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useAppContext } from '@contexts/AppContext';
import dayjs from 'dayjs';
import { groupBy, orderBy, uniq, uniqBy } from 'lodash';
import pluralize from 'pluralize';

import API from '@api/API';

import { useAppSettingsQuery } from '@queries/AppQueries';
import { useCurrentEntertainerQuery, useEntertainerDirectory } from '@queries/EntertainerQueries';
import { useUnauthorizedEventsQuery } from '@queries/EventQueries';
import { useFeaturedLooksFeedPostsQuery } from '@queries/PostQueries';
import {
  Series,
  useSeriesByTagId,
  useSeriesByTagSlug,
  useSeriesEntertainersQuery,
} from '@queries/TagQueries';
import { useCurrentUser } from '@queries/UserQueries';

import { useSendEntertainerBadge } from '@mutations/EntertainerMutations';

import { useAnalytics } from '@hooks/useAnalytics';
import { CurrentCity, useCitiesQuery, useCityById } from '@hooks/useCurrentUserLocation';
import { useDebounce } from '@hooks/useDebounce';
import { useIsVisible } from '@hooks/useIsVisible';

import {
  EntertainerDashboardAction,
  useLocation,
  useNavigate,
  useParams,
  useRouter,
} from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';
import { mapStringToParagraphs } from '@utilities/mapStringToParagraphs';
import { openExternalUrl } from '@utilities/openExternalUrl';

import Button from '@components/buttons/Button';
import { ShareLinkButton } from '@components/buttons/ShareLinkButton';
import EventCard, {
  EventActions,
  EventHeader,
  EventImage,
  PromoterHeader,
} from '@components/cards/EventCard';
import { VenueCard } from '@components/cards/VenueCard';
import { ImageUploadField } from '@components/form/ImageUploadField';
import { Input } from '@components/form/Input';
import { Select } from '@components/form/Select';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { AsyncImage } from '@components/layout/AsyncImg';
import { ButtonFilter } from '@components/layout/ButtonFilter/ButtonFilter';
import Card from '@components/layout/Card/Card';
import { useDialog } from '@components/layout/Dialog/useDialog';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { ErrorBoundary } from '@components/layout/ErrorBoundary';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';
import { LinkBanner } from '@components/layout/LinkBanner/LinkBanner';
import { Popup } from '@components/layout/Popup/Popup';
import { Screen } from '@components/layout/Screen/Screen';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { useAuthGate } from '@screens/LogInScreen/AuthGateContext';
import { SeriesEntertainersList } from '@screens/series/SeriesScreen/SeriesScreen';
import {
  SweepstakesCard,
  useSweepstakesReferralLink,
} from '@screens/series/SeriesScreen/SweepstakesCard';

import { CitySelector } from '../DragKingCommunityScreen/DragKingThingShoutoutsScreen';
import { KingOfDragBanner } from '../DragKingCommunityScreen/KingOfDragBanner';
import { ScheduledSocialPost } from '../DragKingCommunityScreen/ScheduledSocialPost';
import { useCityDragKingSeriesEntertainers } from '../DragKingCommunityScreen/useCityDragKingSeriesEntertainers';
import { useCityDragKingSeriesEvents } from '../DragKingCommunityScreen/useCityDragKingSeriesEvents';
import { useCityDragKingSeriesMostBookedEntertainers } from '../DragKingCommunityScreen/useCityDragKingSeriesMostBookedEntertainers';
import { FeaturedKing } from '../EntertainerDetailsScreen/assets/FeaturedKing';
import { LandingPageSweepstakesCard } from '../EntertainerSweepstakesScreen/EntertainerSweepstakesScreen';
import { FeaturedLook, useFeaturedLooksQuery } from '../FeaturedLooksScreen/FeaturedLooksScreen';
import {
  AddFeaturedPhotoPopup,
  AddImageContent,
  FeaturedPhotoManagementSection,
} from '../components/FeaturedPhotoManagementSection';
import tickets from './tickets.svg';

interface DragKingCommunityScreenProps {}

interface DragKingCommunitySubmission {
  _id: string;
  title: string;
  image?: string;
  status: string;
  entertainer?: {
    _id: string;
    title: string;
    bookingHandle: string;
  };
}

const useDragKingCommunitySubmissions = (isEnabled: boolean) => {
  const { data, isFetched } = useQuery({
    queryKey: ['dragKingCommunitySubmissions'],
    queryFn: () =>
      API.get<{ submissions: DragKingCommunitySubmission[] }>(
        `/v1/drag-king-community/submissions`,
      ),
    enabled: isEnabled,
  });

  return {
    submissions: data?.submissions,
    areSubmissionsReady: isFetched,
  };
};

const useUpdateDragKingCommunitySubmission = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({
      entertainerMediaId,
      status,
      temp,
    }: {
      entertainerMediaId: string;
      status: 'approved' | 'rejected';
      temp?: {
        entertainerInstagram?: string;
        base64Image?: any;
        path?: string;
        title?: string;
        addCollaborator?: boolean;
      };
    }) =>
      API.post(`/v1/drag-king-community/submissions`, {
        entertainerMediaId,
        status,
        temp,
      }),
  });

  return {
    isUpdatingSubmission: isPending,
    updateSubmissionAsync: mutateAsync,
  };
};

const DragKingSubmissionCard: FC<{
  submission: DragKingCommunitySubmission;
}> = ({ submission }) => {
  const [status, setStatus] = useState(submission.status);
  const [title, setTitle] = useState(submission.entertainer?.title);
  const { updateSubmissionAsync } = useUpdateDragKingCommunitySubmission();

  const handleApprove = async () => {
    setStatus('approved');
    await updateSubmissionAsync({
      entertainerMediaId: submission._id,
      status: 'approved',
      temp: {
        path: submission.image, // for the sake of the API, we need to send the image path
        title, // this is required for the API to update the title of the media
      },
    });
  };

  const handleReject = async () => {
    setStatus('rejected');
    await updateSubmissionAsync({
      entertainerMediaId: submission._id,
      status: 'rejected',
      temp: {
        path: submission.image, // for the sake of the API, we need to send the image path
        title, // this is required for the API to update the title of the media
      },
    });
  };

  return (
    <div className="d-grid gap-2">
      <img
        src={submission.image}
        alt={submission.title}
        className="w-100"
        style={{ borderRadius: 8 }}
      />
      <Input
        defaultValue={submission.entertainer.title}
        onChange={(e) => setTitle(e.target.value)}
      />
      <div className="d-flex gap-2">
        <Button
          roundness="rounded"
          color="neutral"
          variant={status === 'approved' ? 'default' : 'outlined'}
          onClick={handleApprove}
        >
          Approve
        </Button>
        <Button
          roundness="rounded"
          color="neutral"
          variant={status === 'rejected' ? 'default' : 'outlined'}
          onClick={handleReject}
        >
          Reject
        </Button>
      </div>
    </div>
  );
};

const AddCustomDragKingSubmissionPopupContent: FC<{ onClose: () => void }> = ({ onClose }) => {
  const [entertainerInstagram, setInstagram] = useState('');
  const [base64Image, setUpdatedImage] = useState<string | ArrayBuffer>();
  const [title, setTitle] = useState('');
  const [status, setStatus] = useState('approved');
  const [addCollaborator, setAddCollaborator] = useState(true);

  const { isUpdatingSubmission, updateSubmissionAsync } = useUpdateDragKingCommunitySubmission();

  const handleSubmit = async () => {
    await updateSubmissionAsync({
      entertainerMediaId: '',
      status: status as any,
      temp: {
        entertainerInstagram,
        base64Image,
        title,
        addCollaborator,
      },
    });

    onClose();
  };

  return (
    <div className="d-grid gap-2">
      <Select
        options={[
          { value: 'approved', label: 'Approved' },
          { value: 'pending', label: 'Pending' },
        ]}
        value={status}
        onChange={(e) => setStatus(e.target.value)}
      />
      <Input
        placeholder="Stage Name *"
        name="stageName"
        onChange={(event) => setTitle(event.target.value)}
      />
      <Input
        placeholder="Instagram *"
        name="instagram"
        onChange={(event) => setInstagram(event.target.value)}
      />
      <div className="d-flex gap-2 align-items-center">
        <Toggle defaultChecked={addCollaborator} onToggleChange={setAddCollaborator} />
        <div>
          <strong>Add Collaborator</strong>
        </div>
      </div>
      <Card>
        <ImageUploadField
          name="customSubmissionImage"
          placeholder="Select an image"
          onChange={(blob) => setUpdatedImage(blob)}
        />
      </Card>
      <div>
        <Button
          roundness="rounded"
          disabled={!base64Image || !title || isUpdatingSubmission}
          onClick={handleSubmit}
        >
          {isUpdatingSubmission ? 'Uploading...' : 'Upload'}
        </Button>
      </div>
    </div>
  );
};

const AddCustomDragKingSubmissionPopup: FC<{ isVisible: boolean; onClose: () => void }> = ({
  isVisible,
  onClose,
}) => {
  return (
    <Popup title="Add Custom" isVisible={isVisible} onClose={onClose} height="full">
      <Popup.Content>
        {isVisible && <AddCustomDragKingSubmissionPopupContent onClose={onClose} />}
      </Popup.Content>
    </Popup>
  );
};

const useShareEntertainerMediaPostMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({ entertainerMediaId, title }: { entertainerMediaId: string; title?: string }) =>
      API.post(`/v1/entertainer-media/${entertainerMediaId}/share-badges-post`, {
        title,
      }),
  });

  return {
    isSharing: isPending,
    shareEntertainerMediaPostAsync: mutateAsync,
  };
};

const FeaturedLookGrid: FC<{
  isLoading: boolean;
  featuredLooks: FeaturedLook[];
  onSelectLook: (lookId: string) => void;
}> = ({ isLoading, featuredLooks, onSelectLook }) => {
  return (
    <>
      {!isLoading ? (
        <div className="d-grid grid-cols-3 gap-1 px-1">
          {featuredLooks.map(({ _id, image }, i) => (
            <div key={_id} style={{ aspectRatio: 1 }} onClick={() => onSelectLook(_id)}>
              <AsyncImage
                src={image}
                style={{
                  display: 'block',
                  height: '100%',
                  width: '100%',
                  objectFit: 'cover',
                }}
              />
            </div>
          ))}
        </div>
      ) : (
        <Skeleton height={400} />
      )}
    </>
  );
};

const LoadingMore: FC<{ onLoadMore: () => void }> = ({ onLoadMore }) => {
  const divRef = useRef<HTMLDivElement>();
  const didLoadMore = useRef(false);
  const { isVisible } = useIsVisible(divRef);

  useEffect(() => {
    if (isVisible && !didLoadMore.current && divRef.current) {
      didLoadMore.current = true;
      requestAnimationFrame(() => {
        onLoadMore();
        didLoadMore.current = false;
      });
    }
  }, [isVisible]);

  return <div ref={divRef} />;
};

export const SuggestedTabContent: FC<{
  isInfiniteScrollEnabled?: boolean;
  onSelectLook: (lookId: string) => void;
}> = ({ isInfiniteScrollEnabled = false, onSelectLook }) => {
  const [page, setPage] = useState(1);
  const { featuredLooksAreReady, featuredLooks = [] } = useFeaturedLooksQuery({});

  const hasMoreLooks = featuredLooks.length > 3 * 8 * page;
  const numberOfRows = 8 * page;

  if (!featuredLooksAreReady) {
    return <Skeleton height={400} />;
  }

  return (
    <>
      <FeaturedLookGrid
        isLoading={!featuredLooksAreReady}
        featuredLooks={featuredLooks
          .filter((featuredLook) => featuredLook.entertainer.isDragKingThing)
          .slice(0, 3 * numberOfRows)}
        onSelectLook={onSelectLook}
      />
      {isInfiniteScrollEnabled && hasMoreLooks ? (
        <LoadingMore onLoadMore={() => setPage(page + 1)} />
      ) : (
        <div className="text-center mt-4">End of suggestions</div>
      )}
    </>
  );
};

export const FavoritesTabContent: FC<{
  isInfiniteScrollEnabled?: boolean;
  badgeId: string;
  onSelectLook: (lookId: string) => void;
  emptyState: ComponentProps<typeof EmptyState>;
}> = ({ isInfiniteScrollEnabled = false, badgeId, emptyState, onSelectLook }) => {
  const [page, setPage] = useState(1);
  const { featuredLooksAreReady, featuredLooks = [] } = useFeaturedLooksQuery(
    { badgeId },
    {
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: true,
    },
  );

  const hasMoreLooks = featuredLooks.length > 3 * 8 * page;
  const numberOfRows = 8 * page;

  if (!featuredLooksAreReady) {
    return <Skeleton height={400} />;
  }

  if (featuredLooks.length === 0) {
    return <EmptyState {...emptyState} />;
  }

  return (
    <>
      <FeaturedLookGrid
        isLoading={!featuredLooksAreReady}
        featuredLooks={featuredLooks
          .filter((featuredLook) => featuredLook.entertainer.isDragKingThing)
          .slice(0, 3 * numberOfRows)}
        onSelectLook={onSelectLook}
      />
      {isInfiniteScrollEnabled && hasMoreLooks ? (
        <LoadingMore onLoadMore={() => setPage(page + 1)} />
      ) : (
        <div className="text-center mt-4">End of favorites</div>
      )}
    </>
  );
};

export const JoinTabContent = () => {
  const [cityId, setCityId] = useState<string>();
  const [didSubmitCity, setDidSubmitCity] = useState(false);
  const { city } = useCityById(cityId);
  const { entertainer } = useCurrentEntertainerQuery();
  const { track } = useAnalytics();

  if (!entertainer?._id) {
    if (!didSubmitCity) {
      return (
        <CitySelector
          isInCitySelectorMode={false}
          onboardingCategories={['Drag King']}
          cityId={cityId}
          onChangeCityId={setCityId}
          onContinue={() => {
            track('Select City', {
              cityName: city?.name,
            });
            setDidSubmitCity(true);
          }}
          onIsProcessingCity={() => {
            setDidSubmitCity(true);
          }}
        />
      );
    }

    return (
      <AddImageContent
        cityId={cityId}
        onSave={() => {}}
        entertainerCategories={['Drag King']}
        meta={{
          channel: 'topkingmaterial',
        }}
      />
    );
  }

  return (
    <FeaturedPhotoManagementSection
      hideTitleToolbar
      hideSecondaryButton
      cityId={cityId}
      featuredMedia={entertainer?.featuredMedia ?? []}
    />
  );
};

const TOP_KING_MATERIAL_CITY_ID_KEY = 'topKingMaterialCityId';

export const DirectoryTabContent: FC<{ onSelectLook: (lookId: string) => void }> = ({
  onSelectLook,
}) => {
  const [cityId, setCityId] = useState(localStorage.getItem(TOP_KING_MATERIAL_CITY_ID_KEY));
  const [entertainerId, setEntertainerId] = useState<string>('');
  const { featuredLooksAreReady, featuredLooks: allFeaturedLooks = [] } = useFeaturedLooksQuery({});
  const { citiesAreReady, cities = [] } = useCitiesQuery();
  const { openConfirmDialog } = useDialog();
  const { track } = useAnalytics();

  const updateCityCallback = (updatedCityId: string) => {
    track('Change Directory City', {
      cityId: updatedCityId,
    });
    localStorage.setItem(TOP_KING_MATERIAL_CITY_ID_KEY, updatedCityId);
    setCityId(updatedCityId);
    setEntertainerId('');
  };

  const featuredLooks = useMemo(() => {
    return allFeaturedLooks.filter(({ entertainer }) => entertainer.isDragKingThing);
  }, [allFeaturedLooks]);

  const entertainers = useMemo(() => {
    const unsortedEntertainers = uniqBy(
      featuredLooks.map((featuredLook) => featuredLook?.entertainer).filter(Boolean),
      '_id',
    );

    return orderBy(unsortedEntertainers, ({ title }) => title?.toLowerCase(), 'asc');
  }, [featuredLooks]);

  const filteredEntertainers = useMemo(() => {
    if (!cityId) {
      return entertainers;
    }

    return entertainers.filter((entertainer) => entertainer.cityIds?.includes(cityId));
  }, [cityId, entertainers]);

  const handleUpdateCityId = (updatedCityId: string) => {
    if (entertainerId) {
      const entertainer = entertainers.find((entertainer) => entertainer._id === entertainerId);

      if (entertainer && !entertainer.cityIds?.includes(updatedCityId)) {
        openConfirmDialog({
          title: 'Change City',
          text: `The current entertainer is not in this selected city. Are you sure you want to change the city?`,
          onConfirm: () => updateCityCallback(updatedCityId),
        });
      }
    } else {
      updateCityCallback(updatedCityId);
    }
  };

  const handleUpdateEntertainer = (updatedEntertainerId: string) => {
    track('Change Directory Entertainer', {
      entertainerId: updatedEntertainerId,
    });
    setEntertainerId(updatedEntertainerId);
  };

  const filteredCities = useMemo(() => {
    const cityIds = featuredLooks.flatMap(
      (featuredLook) => featuredLook?.entertainer?.cityIds ?? [],
    );

    const uniqueCityIds = Object.fromEntries(cityIds.map((id) => [id, true]));

    return cities.filter((city) => uniqueCityIds[city._id]);
  }, [cities, featuredLooks]);

  const filteredFeaturedLooks = useMemo(() => {
    if (!cityId && !entertainerId) {
      return featuredLooks;
    }

    return featuredLooks.filter(({ entertainer }) => {
      if (!entertainer) return false;

      if (entertainerId && entertainer._id !== entertainerId) {
        return false;
      }
      if (cityId && !entertainer.cityIds?.includes(cityId)) {
        return false;
      }
      return true;
    });
  }, [cityId, entertainerId, featuredLooks]);

  const filteredFeaturedLooksGroupedByEntertainer = useMemo(() => {
    return groupBy(
      orderBy(filteredFeaturedLooks, (look) => look.entertainer?.title, 'asc'),
      (look) => look.entertainer?.title,
    );
  }, [filteredFeaturedLooks]);

  return (
    <div>
      <div className="d-flex gap-2">
        <Select
          options={[
            { value: '', label: '-- All Entertainers --' },
            ...filteredEntertainers.map((entertainer) => ({
              value: entertainer._id,
              label: entertainer.title,
            })),
          ]}
          value={entertainerId}
          onChange={(e) => handleUpdateEntertainer(e.target.value)}
        />
        <Select
          options={[
            { value: '', label: '-- All Cities --' },
            ...filteredCities.map((city) => ({ value: city._id, label: city.name })),
          ]}
          value={cityId}
          onChange={(e) => handleUpdateCityId(e.target.value)}
        />
      </div>
      <div className="mt-4">
        {entertainerId || cityId ? (
          Object.entries(filteredFeaturedLooksGroupedByEntertainer).map(([title, looks]) => (
            <>
              <TitleToolbar
                text={title}
                size="sm"
                action={
                  <>
                    {Array.isArray(looks)
                      ? `${looks.length} ${pluralize('look', looks.length)}`
                      : undefined}
                  </>
                }
              />
              <FeaturedLookGrid
                isLoading={!featuredLooksAreReady || !citiesAreReady}
                featuredLooks={Array.isArray(looks) ? looks : []}
                onSelectLook={onSelectLook}
              />
            </>
          ))
        ) : (
          <EmptyState title="Browse Directory" text="Select a filter to get started" />
        )}
      </div>
    </div>
  );
};

export const TopKingMaterialPopup: FC<{
  isVisible: boolean;
  onClose: () => void;
  entertainerMedia: {
    _id: string;
    title: string;
    image: string;
  };
  entertainer: {
    _id: string;
    title: string;
  };
  header?: ReactElement;
}> = ({ entertainerMedia, entertainer, isVisible, onClose, header }) => {
  const { track } = useAnalytics();
  const { createAuthGateHandler } = useAuthGate();
  const { sendEntertainerBadgeAsync, isSubmitting } = useSendEntertainerBadge();
  const [didSend, setDidSend] = useState(false);

  const headerImageSize = 64;
  const handleNominate = () => {
    track('Nominated', {
      campaign: 'Top King Material',
      entertainerId: entertainer._id,
      entertainerMediaId: entertainerMedia._id,
    });

    createAuthGateHandler(
      async () => {
        await sendEntertainerBadgeAsync({
          entertainerId: entertainer._id,
          entertainerMediaId: entertainerMedia._id,
          badgeId: '68520374266f2ca8333487ae',
          reason: 'Look',
          meta: {},
        });
        setDidSend(true);
      },
      {
        entertainerCallback: () => {},
      },
    );
  };

  return (
    <Popup isVisible={isVisible} onClose={onClose} title="Top King Material" height="full">
      <Popup.Content>
        {entertainerMedia && (
          <>
            <TitleToolbar
              className="mb-2"
              size="md"
              text={entertainer.title}
              action={
                <Button
                  className="py-2 px-3"
                  disabled={isSubmitting || didSend}
                  roundness="rounded"
                  size="sm"
                  onClick={handleNominate}
                >
                  {didSend ? 'Nominated' : 'Nominate'}
                </Button>
              }
            />
            <div style={{ position: 'relative' }}>
              <AsyncImage src={entertainerMedia.image} style={{ width: '100%' }} />
            </div>

            <div style={{}}>{entertainer.title}</div>
            <div className="mb-3" style={{ fontSize: 24, lineHeight: 1.2 }}>
              <strong>{entertainerMedia.title}</strong>
            </div>
            <div style={{ borderRadius: 8, background: 'rgba(255,255,255,.1)' }}>
              <div className="p-2">
                <div className="d-flex gap-3 align-items-center">
                  <div>
                    <AsyncImage
                      src="https://gaggl.app/api/public/assets/images/badges/top-king-material.png"
                      placeholder={
                        <div style={{ height: headerImageSize, width: headerImageSize }} />
                      }
                      style={{
                        display: 'block',
                        height: headerImageSize,
                        width: headerImageSize,
                        objectFit: 'cover',
                      }}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 16, lineHeight: 1.2 }}>
                      <strong>Is this Top King Material?</strong>
                    </div>
                    <div>Nominate this look to help celebrate overlooked local artists!</div>
                  </div>
                </div>
                <div className="mt-2">
                  <Button
                    disabled={isSubmitting || didSend}
                    roundness="rounded"
                    onClick={handleNominate}
                  >
                    {didSend ? 'Nominated' : 'Nominate this look'}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </Popup.Content>
    </Popup>
  );
};

const VIEW_AS_USER = false;

interface CityEntertainerRecommendationGroupsScreenProps {}

export const TopKingMaterialScreen =
  createScreenComponent<CityEntertainerRecommendationGroupsScreenProps>(({ f7route }) => {
    const { track } = useAnalytics();
    const [tabValue, setTabValue] = useState('suggested');

    const { featuredLooksAreReady, featuredLooks = [] } = useFeaturedLooksQuery({});

    const [selectedLookId, setSelectedLookId] = useState<string>();
    const currentFeaturedLook = useMemo<FeaturedLook | undefined>(() => {
      return featuredLooks.find(({ _id }) => _id === selectedLookId);
    }, [selectedLookId, featuredLooks]);

    const handleSelectLook = (lookId: string) => {
      track('Look Selected', {
        campaign: 'Top King Material',
        lookId,
      });
      setSelectedLookId(lookId);
    };

    const headerImageSize = 64;
    const isAfterPremier = dayjs().isAfter('2025-06-22', 'date');

    return (
      <Screen
        name="City Entertainer Recommendation Groups Screen"
        hideToolbar
        headerProps={{
          title: <Screen.GagglTitle />,
          right: <ShareLinkButton url={`https://gaggl.app/top-king-material`} />,
        }}
      >
        <Screen.Content>
          <div className="mb-3">
            <KingOfDragBanner context="Top King Material Screen" />
          </div>
          <div className="d-flex mb-3 gap-3 align-items-center">
            <div>
              <AsyncImage
                src="https://gaggl.app/api/public/assets/images/badges/top-king-material.png"
                placeholder={<div style={{ height: headerImageSize, width: headerImageSize }} />}
                style={{
                  display: 'block',
                  height: headerImageSize,
                  width: headerImageSize,
                  objectFit: 'cover',
                }}
              />
            </div>
            <div>
              <strong className="fs-5">Top King Material</strong>
              <br />
              <span>Crowning kings of local drag 👑</span>
            </div>
          </div>

          <p className="mt-0">
            {isAfterPremier ? 'Enjoying "King of Drag"? ' : 'Excited for "King of Drag"? '}
            <strong>Gaggl</strong> is here to celebrate the drag artists who don't always get the
            spotlight — kings, things, and everything in between.
          </p>

          <p>
            Think you have <strong>Top King Material</strong> in your community? Nominate some of
            the suggested looks, browse local talent in the directory, or upload your own!
          </p>
          <p>
            Every week, we'll highlight some of the most community-selected artists on Instagram —
            giving them the spotlight and recognition they deserve.
          </p>

          <ButtonFilter
            selected={tabValue}
            options={[
              { value: 'suggested', label: 'Suggested' },
              { value: 'directory', label: 'Directory' },
              { value: 'join', label: 'Upload' },
            ]}
            onChange={(value) => {
              track('Tab Changed', { tab: value });
              setTabValue(value);
            }}
          />

          {tabValue === 'suggested' && <SuggestedTabContent onSelectLook={handleSelectLook} />}
          {tabValue === 'directory' && <DirectoryTabContent onSelectLook={handleSelectLook} />}
          {tabValue === 'join' && <JoinTabContent />}
        </Screen.Content>
        <TopKingMaterialPopup
          isVisible={!!selectedLookId}
          onClose={() => setSelectedLookId(undefined)}
          entertainerMedia={currentFeaturedLook}
          entertainer={currentFeaturedLook?.entertainer}
        />
      </Screen>
    );
  });
